import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { FiUserCheck, FiUserX, FiInbox, FiHash, FiSearch, FiX, FiMessageCircle } from 'react-icons/fi';
import BASE_URL from '../../../config/config';
import { claves, cabecerasAuth } from '../../../queries/cliente';
import { formatearFechaLimite } from '../../../utils/fechaLimite';
import DtDropdown from './DtDropdown';
import Paginacion from '../../../components/ui/Paginacion';

const DEPARTAMENTOS = ['no_especificado', 'diseno', 'marketing', 'desarrollo', 'redes_sociales'];
const ESTADOS = ['abierto', 'cerrado', 'retenido'];

const TICKETS_PER_PAGE = 5;

/**
 * Cuenta mensajes de staff (admin/técnico) acumulados desde el último
 * mensaje del propio usuario al final del chat (aún no vistos por él).
 */
const contarMensajesNuevos = (chatRaw) => {
  let chat = chatRaw;
  if (typeof chat === 'string') {
    try {
      chat = JSON.parse(chat);
    } catch (err) {
      return 0;
    }
  }
  if (!Array.isArray(chat)) return 0;
  let count = 0;
  for (let i = chat.length - 1; i >= 0; i -= 1) {
    if (chat[i].role === 'user') break;
    count += 1;
  }
  return count;
};

/**
 * Dashboard para clientes: bienvenida + historial de tickets propios
 * con búsqueda, filtro por departamento y paginación.
 */
const DashboardUsuario = ({ tokenInfo, onLinkClick }) => {
  const { t } = useTranslation();
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedEstado, setSelectedEstado] = useState('');

  const hayFiltrosActivos = searchText !== '' || selectedDept !== '' || selectedEstado !== '';

  // Los tickets del propio cliente: mismos datos (y misma caché) que consume
  // la ficha de usuario del panel de administración.
  const consultaTickets = useQuery({
    queryKey: claves.ticketsUsuario(tokenInfo.usuario),
    queryFn: async () => {
      const resp = await axios.get(`${BASE_URL}/tickets/total/${tokenInfo.usuario}`, cabecerasAuth());
      const lista = resp.data.tickets || [];
      return [...lista].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    enabled: Boolean(tokenInfo.usuario),
  });

  const tickets = useMemo(() => consultaTickets.data || [], [consultaTickets.data]);
  const isLoading = consultaTickets.isPending;

  useEffect(() => {
    const filtered = tickets.filter(
      (ticket) =>
        (ticket.asunto.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.identificador.toString().includes(searchText)) &&
        (selectedDept === '' || ticket.dpto.toLowerCase() === selectedDept.toLowerCase()) &&
        (selectedEstado === '' || ticket.estado_del_ticket.toLowerCase() === selectedEstado.toLowerCase())
    );
    setFilteredTickets(filtered);
    setCurrentPage(1);
  }, [searchText, tickets, selectedDept, selectedEstado]);

  const limpiarFiltros = () => {
    setSearchText('');
    setSelectedDept('');
    setSelectedEstado('');
  };

  // Las opciones se rearman con el idioma para que los rótulos se traduzcan
  const opcionesDepartamento = DEPARTAMENTOS.map((value) => ({ value, label: t(`tickets.departamentos.${value}`) }));
  const opcionesEstado = ESTADOS.map((value) => ({ value, label: t(`tickets.estados.${value}`) }));

  const indexOfLast = currentPage * TICKETS_PER_PAGE;
  const indexOfFirst = indexOfLast - TICKETS_PER_PAGE;
  const currentTickets = filteredTickets.slice(indexOfFirst, indexOfLast);

  return (
    <div className="dash-panel">
      <div className="dash-welcome">
        <h1>{t('dashboard.holaUsuario', { nombre: tokenInfo.nombre })}</h1>
        <p>{t('dashboard.textoUsuario')}</p>
      </div>

      <div className="dt-filtros-bar">
        <div className="dt-search">
          <FiSearch className="dt-search-icon" />
          <input
            type="text"
            className="dt-search-input"
            placeholder={t('tickets.buscarPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <DtDropdown
          options={opcionesDepartamento}
          value={selectedDept}
          onChange={setSelectedDept}
          placeholder={t('tickets.todosDepartamentos')}
        />
        <DtDropdown
          options={opcionesEstado}
          value={selectedEstado}
          onChange={setSelectedEstado}
          placeholder={t('tickets.todosEstados')}
        />
        {hayFiltrosActivos && (
          <button type="button" className="dt-filtros-limpiar" onClick={limpiarFiltros}>
            <FiX /> {t('tickets.limpiar')}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="dash-tickets-loading">{t('dashboard.cargandoTickets')}</div>
      ) : (
        <div className="dt-table-card dt-table-card--usuario dt-table-card--propios ticket-table-surface">
          <div className="dt-table-header">
            <span>{t('tickets.ticket')}</span>
            <span>{t('tickets.estado')}</span>
            <span>{t('tickets.departamento')}</span>
            <span>{t('tickets.prioridad')}</span>
            <span>{t('tickets.fecha')}</span>
            <span>{t('tickets.fechaLimite')}</span>
            <span>{t('tickets.asignadoA')}</span>
          </div>
          <div className="dt-ticket-list">
            {filteredTickets.length === 0 ? (
              <div className="dt-empty-state">
                <FiInbox />
                {hayFiltrosActivos ? (
                  <>
                    <p>{t('tickets.sinCoincidencias')}</p>
                    <button type="button" className="dt-empty-reset" onClick={limpiarFiltros}>{t('tickets.limpiarFiltros')}</button>
                  </>
                ) : (
                  <p>{t('dashboard.sinTickets')}</p>
                )}
              </div>
            ) : (
              currentTickets.map((ticket) => {
                const mensajesNuevos = ticket.estado_del_chat === 'respondido' && ticket.estado_del_ticket !== 'cerrado'
                  ? contarMensajesNuevos(ticket.chat)
                  : 0;
                return (
                <Link
                  key={ticket.identificador}
                  className="dt-ticket-row"
                  to={`/ticket/${ticket.identificador}`}
                  onClick={onLinkClick}
                >
                  <div className="dt-col-ticket">
                    <div className="dt-ticket-icon"><FiHash /></div>
                    <div className="dt-col-ticket-texto">
                      <span className="dt-ticket-id">#{ticket.identificador}</span>
                      <span className="dt-ticket-asunto">
                        {ticket.asunto.split(' ').slice(0, 6).join(' ')}
                        {ticket.asunto.split(' ').length > 6 ? '...' : ''}
                      </span>
                    </div>
                    {mensajesNuevos > 0 && (
                      <span className="dt-mensajes-nuevos" title={t('tickets.mensajesNuevos', { count: mensajesNuevos })}>
                        <FiMessageCircle />
                        {mensajesNuevos}
                      </span>
                    )}
                  </div>

                  <div className="dt-col-badges">
                    <div className="dt-col-estado">
                      {ticket.estado_del_ticket && (
                        <p className={ticket.estado_del_ticket}>{t(`tickets.estados.${ticket.estado_del_ticket}`)}</p>
                      )}
                    </div>
                    <div className="dt-col-dpto">
                      {ticket.dpto && (
                        <p className={ticket.dpto.replace('_', '-')}>{t(`tickets.departamentos.${ticket.dpto}`)}</p>
                      )}
                    </div>
                    <div className="dt-col-prioridad">
                      {ticket.prioridad === 'no_urgente' && <p className="sin-prioridad">{t('tickets.prioridades.no_urgente')}</p>}
                      {ticket.prioridad === 'urgente' && <p className="alta">{t('tickets.prioridades.urgente')}</p>}
                    </div>
                  </div>

                  <div className="dt-col-fecha">{new Date(ticket.createdAt).toLocaleDateString()}</div>
                  <div className="dt-col-fecha dt-col-limite">{formatearFechaLimite(ticket.fecha_limite) || '—'}</div>
                  <div className="dt-col-asignado">
                    {ticket.usuario_encargado === 'sin_asignar' ? (
                      <>
                        <span className="dt-asignado-icono dt-asignado-icono-vacio"><FiUserX /></span>
                        <p>{t('tickets.sinAsignar')}</p>
                      </>
                    ) : (
                      <>
                        <span className="dt-asignado-icono dt-asignado-icono-activo"><FiUserCheck /></span>
                        <p>@{ticket.usuario_encargado}</p>
                      </>
                    )}
                  </div>
                </Link>
                );
              })
            )}
          </div>
          <Paginacion
            pagina={currentPage}
            porPagina={TICKETS_PER_PAGE}
            total={filteredTickets.length}
            onCambiar={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default DashboardUsuario;
